import { Box, Typography, Grid, Divider, Button } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SectionHeading from './SectionHeading';

function ageFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
}

function Field({ label, value }) {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    fontWeight: 600,
                    fontSize: 10.5,
                }}
            >
                {label}
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: 'text.primary', mt: 0.25 }}>
                {value || '—'}
            </Typography>
        </Grid>
    );
}

export default function PatientInfoTab({ patient }) {
    const navigate = useNavigate();
    const { userData } = useSelector((s) => s.auth);
    const role = userData?.role || 'admin';

    if (!patient) {
        return <Typography color="text.secondary">Loading patient…</Typography>;
    }

    const age = ageFromDob(patient.dob);
    const dobLabel = patient.dob ? new Date(patient.dob).toLocaleDateString() : null;

    const editPatient = () => {
        navigate(`/${role}/patient-management`, { state: { editPatientId: patient._id } });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <SectionHeading
                    title="Patient information"
                    hint="Demographics and consent contact carried over from the patient record."
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    onClick={editPatient}
                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    Edit patient
                </Button>
            </Box>

            <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10, display: 'block', mb: 1 }}
            >
                Demographics
            </Typography>
            <Grid container spacing={2.5}>
                <Field label="Full name" value={`${patient.fName || ''} ${patient.lName || ''}`.trim()} />
                <Field label="MRN" value={patient.mrn} />
                <Field label="Date of birth" value={dobLabel} />
                <Field label="Age" value={age !== null ? `${age} yrs` : null} />
                <Field label="Gender" value={patient.gender} />
                <Field label="Company / Employer" value={patient.company} />
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10, display: 'block', mb: 1 }}
            >
                Contact
            </Typography>
            <Grid container spacing={2.5}>
                <Field label="Phone" value={patient.contactNumber} />
                <Field label="Email" value={patient.email} />
                <Field label="Address" value={patient.address} />
                <Field label="City / region" value={patient.region} />
                <Field label="Country" value={patient.country} />
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10, display: 'block', mb: 1 }}
            >
                Next of Kin
            </Typography>
            <Grid container spacing={2.5}>
                <Field label="Name" value={patient.emergencyContactName} />
                <Field label="Relation" value={patient.relationShipToPatient} />
                <Field label="Contact number" value={patient.emergencyContactNumber} />
            </Grid>
        </Box>
    );
}
